import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Select from "react-select";
import makeAnimated from "react-select/animated";
import axiosInstance from "../../../../Config/axiosInstance";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddContactPopup from "../ContactPersons/AddContacts";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';

import { Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button } from "@mui/material";
import "./Supplier.css";

const EditSupplier = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: "",
    fiscal_code: "",
    phone: "",
    email: "",
    address: "",
    departments: [],
    contacts: [],
    status: "Active",
    nr_crt: "",
    category: [],
  });

  const [inaccessibleDepartments, setInaccessibleDepartments] = useState([]);
  const [isContactPopupOpen, setContactPopupOpen] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [allContacts, setAllContacts] = useState([]);
  const [existingSupplierInfo, setExistingSupplierInfo] = useState(null);
  const [isDepartmentSelectPopupOpen, setDepartmentSelectPopupOpen] = useState(false);
  const [selectedDepartments, setSelectedDepartments] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [showCategory, setShowCategory] = useState(false);
  const [initialFiscalCode, setInitialFiscalCode] = useState("");
  const [emailError, setEmailError] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const animatedComponents = makeAnimated();

  const statusOptions = [
    { value: "Active", label: "Active" },
    { value: "Inactive", label: "Inactive" },
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        const deptResponse = await axiosInstance.get("/department/user/");
        const departmentOptions = deptResponse.data.map(dept => ({
          value: dept.value,
          label: dept.label,
        }));
        setDepartments(departmentOptions);

        const catResponse = await axiosInstance.get("/category/");
        const categoryOptions = catResponse.data.map(cat => ({
          value: cat.id,
          label: cat.title,
        }));
        setCategoryOptions(categoryOptions);

        const supplierResponse = await axiosInstance.get(`/suppliers/${id}/`);
        const supplier = supplierResponse.data;
        setInitialFiscalCode(supplier.fiscal_code);

        const contactsResponse = await axiosInstance.get(`/supplier-contact/?supplier=${id}`);
        const allContactsData = contactsResponse.data.map(contact => ({
          value: contact.id,
          label: contact.name,
          status: contact.contact_status,
          supplier: supplier.id,
        }));
        const activeContacts = allContactsData.filter(contact => contact.status === "Active");

        const allSupplierDeptIds = supplier.departments || [];

        const visibleDepts = allSupplierDeptIds
          .map(deptId => departmentOptions.find(d => d.value === deptId))
          .filter(Boolean);

        const invisibleDepts = allSupplierDeptIds
          .filter(deptId => !departmentOptions.some(opt => opt.value === deptId));

        setInaccessibleDepartments(invisibleDepts);

        setFormData({
          ...supplier,
          departments: visibleDepts,
          category: supplier.category
            ?.map(catId => categoryOptions.find(c => c.value === catId))
            .filter(Boolean) || [],
          contacts: activeContacts,
        });

        setAllContacts(allContactsData);
      } catch (error) {
        console.error("Eroare la încărcarea datelor:", error);
      }
    };

    fetchData();
  }, [id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddContact = () => {
    setContactPopupOpen(true);
  };

  const handleCloseContactPopup = () => {
    setContactPopupOpen(false);
  };

  const handleChangeContacts = async (selectedContacts) => {
    const removedContacts = formData.contacts.filter(
      (contact) => !selectedContacts.some((selected) => selected.value === contact.value)
    );

    const updatedAllContacts = allContacts.map((contact) => {
      if (removedContacts.some((removed) => removed.value === contact.value)) {
        return { ...contact, status: "Inactive" };
      } else if (selectedContacts.some((selected) => selected.value === contact.value)) {
        return { ...contact, status: "Active" };
      }
      return contact;
    });

    await Promise.all(
      removedContacts.map(async (contact) => {
        try {
          await axiosInstance.patch(`/supplier-contact/${contact.value}/`, {
            contact_status: "Inactive",
          });
        } catch (error) {
          console.error(`Eroare la actualizarea statusului pentru contactul ${contact.label}:`, error);
        }
      })
    );

    await Promise.all(
      selectedContacts.map(async (contact) => {
        if (contact.status === "Inactive") {
          try {
            await axiosInstance.patch(`/supplier-contact/${contact.value}/`, {
              contact_status: "Active",
            });
          } catch (error) {
            console.error(`Eroare la actualizarea statusului pentru contactul ${contact.label}:`, error);
          }
        }
      })
    );

    setAllContacts(updatedAllContacts);
    setFormData((prev) => ({ ...prev, contacts: selectedContacts }));
  };

  const handleAddContactToList = async (newContact) => {
    if (newContact.id) {
      setFormData((prev) => ({
        ...prev,
        contacts: [...prev.contacts, { value: newContact.id, label: newContact.name }],
      }));
      return;
    }

    if (id) {
      try {
        const response = await axiosInstance.post("/supplier-contact/", {
          ...newContact,
          supplier: id,
        });

        setFormData((prev) => ({
          ...prev,
          contacts: [...prev.contacts, { value: response.data.id, label: response.data.name }],
        }));
      } catch (error) {
        console.error("Eroare la adăugarea contactului:", error.response?.data || error.message);
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        contacts: [...prev.contacts, newContact],
      }));
    }
  };

  const checkFiscalCode = async (fiscalCode) => {
    try {
      const response = await axiosInstance.get(`/check_fiscal_code/?fiscal_code=${fiscalCode}`);
      if (response.data.exists) {
        setExistingSupplierInfo(response.data);
      } else {
        setExistingSupplierInfo(null);
      }
    } catch (error) {
      console.error("Error checking fiscal code:", error);
    }
  };

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      if (
        formData.fiscal_code &&
        formData.fiscal_code.length > 4 &&
        formData.fiscal_code !== initialFiscalCode
      ) {
        checkFiscalCode(formData.fiscal_code);
      } else {
        setExistingSupplierInfo(null);
      }
    }, 300);
    return () => clearTimeout(delayDebounce);
  }, [formData.fiscal_code, initialFiscalCode]);

  const isProcurementSelected = () => {
    const procurementDept = departments.find(
      (dept) => dept.label.toLowerCase() === "procurement"
    );
    if (!procurementDept) return false;
    return formData.departments.some((d) => d.value === procurementDept.value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    let hasError = false;

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setEmailError("**Please enter a valid email address.");
      hasError = true;
    } else {
      setEmailError("");
    }

    const phoneRegex = /^\+?[0-9]\d{1,19}$/;
    if (!phoneRegex.test(formData.phone)) {
      setPhoneError("**Please enter a valid phone number.");
      hasError = true;
    } else {
      setPhoneError("");
    }

    if (hasError) {
      return;
    }

    if (isProcurementSelected() && (!formData.category || formData.category.length === 0)) {
      alert("The 'Category' field is required when the 'Procurement' department is selected.");
      return;
    }

    try {
      const dataToSend = {
        ...formData,
        departments: [
          ...formData.departments.map(d => d.value),
          ...inaccessibleDepartments,
        ],
        category: formData.category.map(cat => cat.value),
        contacts: formData.contacts.map(c => c.value),
        department_statuses: formData.departments.map(dep => ({
          department: dep.value,
          status: formData.status,
        })),
      };

      await axiosInstance.put(`/suppliers/${id}/`, dataToSend);
      navigate(-1);

    } catch (error) {
      alert(JSON.stringify(error.response?.data || error.message, null, 2));
    }
  };


  return (
    <>
      <div className="supplier">
        <div className="add-supplier-container">
          <div className="supplier-header">
            <ArrowBackIcon className="back-arrow" onClick={() => navigate(-1)} />
            <h2 className="add-supplier-title">Edit Supplier</h2>
          </div>
        </div>

        <hr className="title-separator" />

        <div className="add-supplier-container">
          <form onSubmit={handleSubmit} className="add-supplier-form">
            <div className="form-group">
              <label className="label">
                Name<span className="required-star">*</span></label>
              <input type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="input"
                required />
            </div>

            <div className="form-group-row">
              <div className="form-group">
                <label className="label">
                  CUI<span className="required-star">*</span></label>
                <input
                  type="text"
                  name="fiscal_code"
                  value={formData.fiscal_code}
                  onChange={handleChange}
                  className="input"
                  required />
                {phoneError &&
                  <span className="supplier-error">&nbsp;</span>
                }
              </div>

              <div className="form-group">
                <label className="label">
                  Phone<span className="required-star">*</span></label>
                <input
                  type="text"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="input"
                  required
                />
                {phoneError &&
                  <span className="supplier-error">{phoneError}</span>
                }
              </div>
            </div>

            <div className="form-group">
              <label className="label">
                Email<span className="required-star">*</span></label>
              <input
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="input"
                required
              />
              {emailError &&
                <span className="supplier-error">{emailError}</span>
              }
            </div>

            <div className="form-group">
              <label className="label">
                Address<span className="required-star">*</span></label>
              <textarea
                name="address"
                value={formData.address}
                onChange={handleChange}
                className="textarea"
                required
              />
            </div>

            <div className="form-group">
              <label className="label">
                Departments<span className="required-star">*</span></label>
              <Select
                options={departments}
                value={formData.departments}
                onChange={(selected) =>
                  setFormData((prev) => ({ ...prev, departments: selected }))
                }
                isMulti
                components={animatedComponents}
                className="multiselect-field"
                required
              />
            </div>

            {isProcurementSelected() && (
              <div className="form-group">
                <label className="label">
                  Category<span className="required-star">*</span>
                </label>
                <Select
                  options={categoryOptions}
                  value={formData.category}
                  onChange={(selected) =>
                    setFormData((prev) => ({ ...prev, category: selected }))
                  }
                  isMulti
                  components={animatedComponents}
                  className="multiselect-field"
                />
              </div>
            )}

            <div className="form-group-row">
              <div className="form-group select-container">
                <label htmlFor="contacts" className="label">Contact Persons</label>
                <Select
                  id="contacts"
                  name="contacts"
                  options={allContacts}
                  value={formData.contacts}
                  onChange={handleChangeContacts}
                  isMulti
                  components={animatedComponents}
                  className="multiselect-field"
                />
              </div>

              <button type="button" className="button add-contact" onClick={handleAddContact}>
                Add Contact
              </button>
            </div>

            <div className="form-group-row">
              <div className="form-group">
                <label className="label">
                  Status<span className="required-star">*</span></label>
                <Select
                  name="status"
                  options={statusOptions}
                  value={statusOptions.find(opt => opt.value === formData.status)}
                  onChange={(selectedOption) =>
                    setFormData({ ...formData, status: selectedOption.value })
                  }
                  className="multiselect-field"
                  components={animatedComponents}
                  isClearable={false}
                  isSearchable={false}
                  required
                />
              </div>

              <div className="button-group-inline">
                <button type="button" className="button discard" onClick={() => navigate(-1)}>
                  Discard
                </button>
                <button type="submit" className="button save">Save</button>
              </div>
            </div>
          </form>

          <AddContactPopup
            isOpen={isContactPopupOpen}
            onClose={handleCloseContactPopup}
            onAddContact={handleAddContactToList}
            supplierId={id}
          />

          {existingSupplierInfo && (
            <Dialog
              open={Boolean(existingSupplierInfo)}
              onClose={() => setExistingSupplierInfo(null)}
            >
              <DialogTitle sx={{ px: 3 }}>
                A supplier with this CUI already exists!
              </DialogTitle>
              <DialogContent sx={{ px: 3 }}>
                <DialogContentText>
                  The supplier <strong>"{existingSupplierInfo?.name}"</strong> with CUI <strong>"{existingSupplierInfo?.fiscal_code}"</strong> already exists in the system.
                  It is currently associated with the following departments:
                  <strong>
                    {existingSupplierInfo?.departments.map(dep => dep.name).join(", ")}
                  </strong>.
                  <br /><br />
                  Would you like to associate this supplier with additional departments?
                </DialogContentText>
              </DialogContent>
              <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'flex-end', gap: 1.5 }}>
                <Button
                  onClick={() => {
                    setDepartmentSelectPopupOpen(true);
                  }}
                  variant="outlined"
                  color="success"
                  startIcon={<CheckCircleIcon />}
                >
                  Yes
                </Button>
                <Button
                  onClick={() => setExistingSupplierInfo(null)}
                  variant="outlined"
                  color="error"
                  startIcon={<CancelIcon />}
                >
                  No
                </Button>
              </DialogActions>
            </Dialog>
          )}

          <Dialog
            open={isDepartmentSelectPopupOpen}
            onClose={() => setDepartmentSelectPopupOpen(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>Select Additional Departments</DialogTitle>
            <DialogContent dividers style={{ minHeight: "500px", overflowY: "auto" }}>
              <DialogContentText>
                Select the departments to associate with the supplier <strong>{existingSupplierInfo?.name}</strong> (CUI: <strong>{existingSupplierInfo?.fiscal_code}</strong>):
              </DialogContentText>

              <div style={{ marginTop: '1rem' }}>
                <Select
                  isMulti
                  options={departments}
                  components={animatedComponents}
                  onChange={(selectedOptions) => {
                    setSelectedDepartments(selectedOptions);
                    const includesProcurement = selectedOptions.some(opt => opt.label.toLowerCase() === 'procurement');
                    setShowCategory(includesProcurement);
                  }}
                />
              </div>

              {showCategory && (
                <div style={{ marginTop: '1rem' }}>
                  <label>Category<span className="required-star">*</span></label>
                  <Select
                    isMulti
                    options={categoryOptions}
                    components={animatedComponents}
                    onChange={(selected) => setSelectedCategories(selected)}
                  />
                </div>
              )}
            </DialogContent>

            <DialogActions>
              <Button
                variant="contained"
                color="primary"
                onClick={async () => {
                  try {
                    if (!existingSupplierInfo?.supplier_id) {
                      console.error("Eroare: ID-ul furnizorului nu a fost găsit.");
                      return;
                    }

                    const existingDepartmentIds = (existingSupplierInfo.departments || []).map(dep => dep.id);
                    const selectedDepartmentIds = selectedDepartments.map(opt => Number(opt.value));
                    const mergedDepartmentIds = [...new Set([...existingDepartmentIds, ...selectedDepartmentIds])];

                    const selectedCategoryIds = selectedCategories.map(cat => Number(cat.value));

                    await axiosInstance.patch(`/suppliers/${existingSupplierInfo.supplier_id}/`, {
                      departments: mergedDepartmentIds,
                      category: selectedCategoryIds,
                    });

                    await axiosInstance.delete(`/suppliers/${id}/`);

                    setDepartmentSelectPopupOpen(false);
                    navigate("/supplier");
                  } catch (error) {
                    console.error("Eroare la actualizare/ștergere:", error.response?.data || error.message);
                    alert("A apărut o eroare la actualizarea sau ștergerea furnizorului.");
                  }
                }}
                startIcon={<CheckCircleIcon />}>
                Save
              </Button>

              <Button
                onClick={() => {
                  setDepartmentSelectPopupOpen(false);
                  setExistingSupplierInfo(null);
                  setShowCategory(false);
                  setSelectedCategories([]);
                  setSelectedDepartments([]);
                }}
                color="inherit"
              >
                Cancel
              </Button>
            </DialogActions>
          </Dialog>
        </div>
      </div>
    </>
  );
};

export default EditSupplier;